import type { ScreeningAnswer } from '@/services/screening';

import type { Localized, ScreeningQuestion, ScreeningSet } from './types';

/**
 * The candidate's answers to the stored set, and what is read off them: whether the set is complete, and
 * the note [03-us-kpw-3a AC-6 … AC-10]. ⛔ Never a route — no answer changes where the visit goes
 * [03-us-kpw-3b; decision 0417].
 *
 * Pure, so the rules a recruiter depends on are testable without a screen — and so the component holds
 * state and nothing else.
 *
 * ⭐ **Held by KEY, never by label** [AC-7, D4]. A language switch re-renders every string from the same
 * payload and cannot touch an answer, because no answer is a string that changes with the language.
 */

/** One question's answer: the chosen option keys, and the text typed into each open follow-up. */
export interface QuestionAnswer {
  readonly chosen: readonly string[];
  /** By option key. Only a CHOSEN option carrying a `followUp` has an entry that counts. */
  readonly followUps: Readonly<Record<string, string>>;
}

export type ScreeningAnswers = Readonly<Record<string, QuestionAnswer>>;

const NO_ANSWER: QuestionAnswer = { chosen: [], followUps: {} };

export const answerOf = (answers: ScreeningAnswers, questionKey: string): QuestionAnswer =>
  answers[questionKey] ?? NO_ANSWER;

/** A tap on an option. `single` replaces the choice; `multi` toggles it. */
export function choose(
  answers: ScreeningAnswers,
  question: ScreeningQuestion,
  optionKey: string,
): ScreeningAnswers {
  const current = answerOf(answers, question.key);
  const chosen =
    question.kind === 'multi'
      ? current.chosen.includes(optionKey)
        ? current.chosen.filter((key) => key !== optionKey)
        : [...current.chosen, optionKey]
      : [optionKey];

  /*
   * An option un-chosen takes its follow-up text with it [AC-6: "the box goes and its text is discarded"].
   * Kept only for what is still chosen, so re-choosing `NO` opens an EMPTY box rather than resurrecting
   * text the candidate abandoned.
   */
  const followUps = Object.fromEntries(
    Object.entries(current.followUps).filter(([key]) => chosen.includes(key)),
  );
  return { ...answers, [question.key]: { chosen, followUps } };
}

/** Where a follow-up's text goes: the question, and the chosen option whose prompt opened the box. */
export interface FollowUpTarget {
  readonly questionKey: string;
  readonly optionKey: string;
}

export function typeFollowUp(
  answers: ScreeningAnswers,
  { questionKey, optionKey }: FollowUpTarget,
  text: string,
): ScreeningAnswers {
  const current = answerOf(answers, questionKey);
  return {
    ...answers,
    [questionKey]: { ...current, followUps: { ...current.followUps, [optionKey]: text } },
  };
}

/** An open follow-up: the chosen option that opened it, and the prompt it asks. */
export interface OpenFollowUp {
  readonly optionKey: string;
  readonly prompt: Localized;
}

/** The follow-ups that are open: chosen options carrying a prompt, in the set's option order. */
export const openFollowUps = (
  question: ScreeningQuestion,
  answer: QuestionAnswer,
): readonly OpenFollowUp[] =>
  question.options.flatMap((option) =>
    option.followUp !== null && answer.chosen.includes(option.key)
      ? [{ optionKey: option.key, prompt: option.followUp }]
      : [],
  );

/** Nothing chosen — including Q2 with no department — is unanswered [AC-8]. */
export const isChoiceMissing = (answer: QuestionAnswer): boolean => answer.chosen.length === 0;

/** An open follow-up left empty is unanswered [AC-8; 0364 (d)]. Blank is empty. */
export const isFollowUpMissing = (answer: QuestionAnswer, optionKey: string): boolean =>
  (answer.followUps[optionKey] ?? '').trim() === '';

/** Every control answered, follow-ups included [0364 (d)]. A completion rule, not a verdict [0111]. */
export const isComplete = (set: ScreeningSet, answers: ScreeningAnswers): boolean =>
  set.questions.every((question) => {
    const answer = answerOf(answers, question.key);
    return (
      !isChoiceMissing(answer) &&
      openFollowUps(question, answer).every((open) => !isFollowUpMissing(answer, open.optionKey))
    );
  });

/**
 * The question as the candidate was asked it, in English: the preamble, a space, then the prompt [AC-9].
 * Stored text, joined and nothing else — no trim, no normalising [the note records the wording AS ASKED,
 * `05-integration-bullhorn-note-fields`].
 */
const askedInEnglish = (question: ScreeningQuestion): string =>
  question.preamble === null ? question.prompt.en : `${question.preamble.en} ${question.prompt.en}`;

/**
 * The note's pairs [AC-9, D5]: one per control in the set's order, then one per open follow-up.
 *
 * ⭐ **English, whatever language is on screen** — the recruiter's copy must not change meaning with the
 * kiosk's language setting. The typed follow-up text goes as typed. ⛔ No key, index or placeholder.
 */
export function toNote(set: ScreeningSet, answers: ScreeningAnswers): ScreeningAnswer[] {
  return set.questions.flatMap((question) => {
    const answer = answerOf(answers, question.key);
    const chosen = question.options.filter((option) => answer.chosen.includes(option.key));
    return [
      // Option order, not tap order: Q2's departments read the same way however they were tapped.
      { question: askedInEnglish(question), answer: chosen.map((o) => o.label.en).join(', ') },
      ...openFollowUps(question, answer).map((open) => ({
        question: open.prompt.en,
        // Only reached on a complete set, where every open box holds text; `''` is never sent in practice.
        answer: answer.followUps[open.optionKey] ?? '',
      })),
    ];
  });
}
